import React from "react";
import { Link, useOutletContext } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { fmt, shortName } from "./data";

export function ProductCard({ p, badge, rail, ribbon }) {
  const caps = (p.storages || []).map((s) => s.label);
  const pct = p.ref > 0 && p.save > 0 ? Math.round((p.save / p.ref) * 100) : 0;
  const promo = (useOutletContext() || {}).promo;
  const pp = promo?.code && promo.percent > 0 && p.price > 0 ? p.price * (1 - promo.percent / 100) : 0;
  return (
    <Link to={`/produit/${p.slug}`} className={`card group block p-3 text-[var(--ink)] ${rail ? "w-[200px] sm:w-[220px]" : ""}`} data-testid={`mq-card-${p.slug}`}>
      <div className="relative aspect-square overflow-hidden rounded-[18px] bg-[var(--tile)] p-4">
        {badge && <span className="absolute left-2.5 top-2.5 rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-[var(--blue)] shadow-sm">{badge}</span>}
        {ribbon && <span className="absolute left-2.5 top-2.5 rounded-full bg-[var(--blue)] px-2 py-0.5 text-[10px] font-semibold text-white shadow-sm" data-testid="mq-card-ribbon">{ribbon}</span>}
        {pct > 0 && <span className="absolute right-2.5 top-2.5 rounded-full bg-[#1a8f4a] px-2 py-0.5 text-[11px] font-bold text-white" data-testid="mq-card-pct">−{pct} %</span>}
        <img src={p.img} alt={p.name} loading="lazy" className="h-full w-full object-contain mix-blend-multiply transition-transform duration-500 group-hover:scale-105" />
      </div>
      <div className="px-1 pb-1 pt-3">
        <div className="line-clamp-1 text-[14px] font-semibold">{shortName(p.name)}</div>
        <div className="mt-0.5 line-clamp-1 text-[12px] text-[var(--subtle)]">{caps.length ? `${caps[0]}${caps.length > 1 ? ` à ${caps[caps.length - 1]}` : ""}` : p.category_name}</div>
        {(p.colors || []).filter((c) => c.hex).length > 1 && (
          <div className="mt-2 flex items-center gap-1" data-testid={`mq-card-colors-${p.slug}`}>
            {p.colors.filter((c) => c.hex).slice(0, 5).map((c) => <span key={c.name} title={c.name} className="h-3.5 w-3.5 rounded-full border border-black/10 shadow-[inset_0_1px_2px_rgba(0,0,0,.15)]" style={{ backgroundColor: c.hex }} />)}
            {p.colors.filter((c) => c.hex).length > 5 && <span className="text-[11px] text-[var(--subtle)]">+{p.colors.filter((c) => c.hex).length - 5}</span>}
          </div>
        )}
        {pp ? (
          <div className="mt-2" data-testid="mq-card-promo">
            <div className="flex flex-wrap items-baseline gap-x-1.5"><span className="text-[11px] text-[var(--subtle)]">dès</span><span className="text-[18px] font-bold text-[var(--blue)]">{fmt(pp)}</span><span className="text-[12px] text-[var(--subtle)] line-through">{fmt(p.price)}</span></div>
            <span className="mt-1 inline-block rounded-full bg-[var(--blue)] px-2 py-0.5 text-[10px] font-semibold text-white">−{promo.percent} % code {promo.code}</span>
          </div>
        ) : <div className="mt-2 flex flex-wrap items-baseline gap-x-1.5"><span className="text-[11px] text-[var(--subtle)]">dès</span><span className="text-[18px] font-bold">{fmt(p.price)}</span>{p.ref > p.price && <span className="text-[12px] text-[var(--subtle)] line-through">{fmt(p.ref)}</span>}</div>}
        <div className="mt-1 flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
          {p.save > 0 ? <span className="save whitespace-nowrap">Économisez {fmt(p.save)}</span> : <span />}
          <span className={`flex items-center gap-1 text-[11px] ${p.stock ? "text-[var(--green)]" : "text-[var(--subtle)]"}`}><span className={`h-1.5 w-1.5 rounded-full ${p.stock ? "bg-[var(--green)]" : "bg-[var(--line)]"}`} />{p.stock ? "En stock" : "Bientôt"}</span>
        </div>
      </div>
    </Link>
  );
}

export function SecHead({ eyebrow, title, accent, link, center }) {
  return (
    <div className={`mb-6 flex flex-wrap items-end gap-4 ${center ? "flex-col items-center text-center" : "justify-between"}`}>
      <div>
        <span className="pill-eyebrow"><Sparkles className="h-3 w-3" />{eyebrow}</span>
        <h2 className="two-tone mt-3 text-[28px] font-semibold leading-tight sm:text-[38px]">{title} {accent && <em>{accent}</em>}</h2>
      </div>
      {link && <Link to={link[1]} className="text-[14px] font-semibold text-[var(--link)] hover:underline" data-testid={`mq-link-${eyebrow}`}>{link[0]} ›</Link>}
    </div>
  );
}

export function ProductGrid({ id, eyebrow, title, accent, items, link }) {
  if (!items.length) return null;
  return (
    <section className="py-10 sm:py-14" data-testid={`mq-grid-${id}`}>
      <div className="wrap">
        <SecHead eyebrow={eyebrow} title={title} accent={accent} link={link} />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-5">
          {items.map((p, i) => <ProductCard key={p.slug} p={p} badge={i === 0 ? "Best-seller" : null} />)}
        </div>
      </div>
    </section>
  );
}

export function ProductRail({ id, eyebrow, title, accent, items, link }) {
  if (!items.length) return null;
  return (
    <section className="py-8 sm:py-10" data-testid={`mq-rail-${id}`}>
      <div className="wrap">
        <SecHead eyebrow={eyebrow} title={title} accent={accent} link={link} />
        <div className="rail">{items.map((p) => <ProductCard key={p.slug} p={p} rail />)}</div>
      </div>
    </section>
  );
}
